import { z } from 'zod';
import { MONEY_PATTERN } from '@miraki/money';
import { zId } from './primitives.js';
import { zReturnLineState } from './consignment.js';

/**
 * Return requests — CN-13, CN-14 (D21, D70, D71, D79). R-25 is the retailer's side, A-43 Admin's.
 *
 * **Frozen before either screen is built**, deliberately. Both are being built at once by
 * different people, and a contract settled afterwards means one of them guessed. The table
 * (`mki_return_requests`, migration 0037) is the authority for what can be stored and this is
 * the authority for what crosses the wire; where they could disagree, the comment says which.
 *
 * Three rules from CN-13 and CN-14 that this shape exists to make unmistakable:
 *
 * 1. **A request does not move the piece.** It stays HELD for the whole life of the request and
 *    so stays in budget use. Nothing here carries a piece status change, and no response field
 *    says "released" until a receipt has happened.
 * 2. **A decision is per piece, not per request** (D71): an approval may cover some of the
 *    pieces. So there is no request-level verdict in either direction, and no `status` field —
 *    whether a request is open, part-decided or finished is read from its lines.
 * 3. **A receipt is not a financial event** (CN-14). There is no credit note, no ledger
 *    reference and no amount owed anywhere in these shapes. What a receipt returns is the
 *    BUDGET released, which is a different thing from money and must not be shown as money owed.
 */

const zDecimal = z.string().regex(MONEY_PATTERN, 'must be a decimal amount');

/**
 * R-25 / CN-13 — what the retailer submits from My Inventory.
 *
 * `remarks` is required (D79) and has a floor of three characters: Admin triages A-43 on the
 * reason and decides a refusal against it (D71), which cannot be weighed against "." The server
 * must additionally refuse a piece that is not HELD, not this retailer's, or already on a line
 * in REQUESTED or APPROVED — the last one because A-43 would otherwise show one piece twice and
 * approving it on both requests means nothing.
 */
export const zReturnRequestCreate = z.object({
  pieceIds: z.array(zId).min(1).max(100),
  remarks: z.string().trim().min(3).max(1000),
  /** CN-13 — an optional note against individual pieces, keyed by piece id. */
  notes: z.record(zId, z.string().trim().max(1000)).optional(),
});
export type ReturnRequestCreate = z.infer<typeof zReturnRequestCreate>;

/**
 * A-43 / D71 — Admin's decision, piece by piece, in one submission.
 *
 * `reason` is required on a refusal and meaningless on an approval, which is why it sits on the
 * line rather than on the request: a single reason field at the top could only describe one of a
 * mixed decision. The server refuses a REFUSED line with no reason.
 */
export const zReturnDecision = z.object({
  lines: z.array(z.object({
    pieceId: zId,
    decision: z.enum(['APPROVE', 'REFUSE']),
    reason: z.string().trim().max(500).optional(),
  })).min(1).max(100),
});
export type ReturnDecision = z.infer<typeof zReturnDecision>;

/**
 * CN-14 — Admin records arrival, piece by piece, because pieces may arrive separately. One call
 * may carry several that arrived together; it is not a whole-request action.
 */
export const zReturnReceipt = z.object({
  pieceIds: z.array(zId).min(1).max(100),
  /** The date they arrived, in the portal's calendar. Defaults to the portal's today. */
  receivedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type ReturnReceipt = z.infer<typeof zReturnReceipt>;

/**
 * CN-09 — Miraki recalls pieces from a retailer: a return request Miraki raises. The same lines and
 * the same receipt (CN-14) as a retailer's request, so the budget comes back the same way. Any
 * HELD piece of that retailer may be recalled, whatever the account's status — a closing account
 * holding stock is exactly where a recall is needed. WHAT TRIGGERS one is D20 and open; that is the
 * portal's `recallTrigger` setting, and this route is the by-hand path that exists whatever it says.
 */
export const zRecallCreate = z.object({
  retailerId: zId,
  pieceIds: z.array(zId).min(1).max(100),
  /** Shown to the retailer on R-25 as the reason. Required, as a retailer's remarks are (D79). */
  remarks: z.string().trim().min(3).max(1000),
  notes: z.record(zId, z.string().trim().max(1000)).optional(),
});
export type RecallCreate = z.infer<typeof zRecallCreate>;

/**
 * Miraki changes its mind about recalled pieces not yet received — or the retailer sold one before
 * the recall reached them and cannot report it while it is recalled. The line becomes WITHDRAWN
 * (by Miraki, on a recall) and the piece, which never left HELD, may be sold again.
 */
export const zRecallCancel = z.object({
  pieceIds: z.array(zId).min(1).max(100),
  reason: z.string().trim().min(3).max(500),
});
export type RecallCancel = z.infer<typeof zRecallCancel>;

/** What a by-hand run of the D20 trigger raised: one recall per retailer with pieces due. */
export interface RecallRun {
  readonly asOf: string;
  readonly raised: readonly { readonly requestId: string; readonly retailerId: string; readonly pieces: number }[];
}

/** One piece on a request, as both screens read it. */
export interface ReturnLine {
  readonly pieceId: string;
  readonly jobNumber: string;
  readonly description: string;
  readonly goldColour: string | null;
  readonly wholesaleValue: string;
  readonly currency: string;
  readonly state: z.infer<typeof zReturnLineState>;
  readonly note: string | null;
  readonly refusalReason: string | null;
  readonly decidedAt: string | null;
  readonly receivedAt: string | null;
  /** CN-07 — how long the retailer held it before asking to send it back. */
  readonly daysHeld: number | null;
}

/**
 * A request as both screens read it. There is no `status`: `open`, `decided` and `complete` are
 * computed from the lines, and are given here as counts rather than as a verdict so neither
 * screen invents its own words for a part-decided request.
 */
export interface ReturnRequestView {
  readonly id: string;
  readonly retailerId: string;
  readonly retailerName: string;
  /**
   * CN-09 — who asked. RETAILER: the retailer's own request (CN-13). MIRAKI: a recall, raised by
   * Admin or by the D20 ageing job; its lines start APPROVED, because Miraki asking for its piece
   * back IS the decision, and so CN-14's rule applies from the start — a recalled piece cannot be
   * marked sold and the retailer cannot withdraw the request.
   */
  readonly origin: 'RETAILER' | 'MIRAKI';
  readonly remarks: string;
  /** Null only on a recall the D20 ageing job raised: no person raised it. */
  readonly requestedBy: string | null;
  readonly requestedAt: string;
  readonly withdrawnAt: string | null;
  readonly lastDecidedAt: string | null;
  readonly lines: readonly ReturnLine[];
  /** Per-state counts over `lines`, so a screen can say "3 approved, 1 refused, 2 back". */
  readonly counts: Readonly<Record<z.infer<typeof zReturnLineState>, number>>;
  /**
   * CN-14 — the wholesale value RELEASED by receipts so far, and what is still out, per
   * currency. Budget, never money: nothing was owed for an unsold piece, so this must not be
   * rendered as a credit or a payable.
   */
  readonly released: readonly { readonly currency: string; readonly amount: string }[];
  /**
   * The value still out on this request: lines in **REQUESTED or APPROVED**, per currency.
   *
   * Stated here because it was a reading rather than a rule, and the two sessions building the
   * two return screens read it differently — one as approved-only, one as both. Both fit "still
   * out", which is why this needed deciding rather than discussing.
   *
   * **REQUESTED counts.** The piece is HELD for the whole life of the request (CN-13) and so is
   * still in budget use, which is the question a retailer is asking when they look at this
   * figure. Counting only approved lines would show a smaller number than the budget those
   * pieces are actually holding.
   *
   * A REFUSED, WITHDRAWN or SOLD_INSTEAD line is not awaiting anything, and a RECEIVED one has
   * moved to `released`.
   */
  readonly awaiting: readonly { readonly currency: string; readonly amount: string }[];
}

/** A-43's queue. `decidedAt: null` first — the ones waiting on Admin (DB-08). */
export interface ReturnQueue {
  readonly asOf: string;
  readonly requests: readonly ReturnRequestView[];
}

/** What R-25 shows: this retailer's own requests, newest first. */
export interface MyReturns {
  readonly asOf: string;
  readonly requests: readonly ReturnRequestView[];
  /** CN-14 — total released to this retailer, so they can see the budget came back. */
  readonly releasedToDate: readonly { readonly currency: string; readonly amount: string }[];
}

/* `zDecimal` is used by the interfaces above through `wholesaleValue` and the amount lists; it is
 * declared once here so a future request schema in this file cannot invent a second money rule. */
void zDecimal;
